import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "es" | "en";

interface Translations {
  [key: string]: {
    es: string;
    en: string;
  };
}

const translations: Translations = {
  // Common
  "common.backToHome": { es: "Volver al inicio", en: "Back to home" },

  // Navigation
  "nav.explore": { es: "Explorar", en: "Explore" },
  "nav.searchBooks": { es: "Catálogo de Libros", en: "Book Catalog" },
  "nav.howItWorks": { es: "Cómo Funciona", en: "How It Works" },
  "nav.community": { es: "Comunidad", en: "Community" },
  "nav.login": { es: "Iniciar Sesión", en: "Log In" },
  "nav.signup": { es: "Registrarse", en: "Sign Up" },

  // Hero
  "hero.title": { es: "Intercambia Libros,", en: "Exchange Books," },
  "hero.titleHighlight": { es: "Comparte Historias", en: "Share Stories" },
  "hero.subtitle": {
    es: "Dale una nueva vida a tus libros favoritos. Únete a la mejor comunidad de intercambio de libros.",
    en: "Give your favorite books a new life. Join the best book exchange community.",
  },
  "hero.cta": { es: "Empezar a Intercambiar", en: "Start Swapping" },
  "hero.ctaSecondary": { es: "Ver Cómo Funciona", en: "See How It Works" },
  "hero.stats.books": { es: "Libros Disponibles", en: "Books Available" },
  "hero.stats.users": { es: "Lectores Activos", en: "Active Readers" },
  "hero.stats.swaps": { es: "Intercambios Exitosos", en: "Successful Swaps" },

  // How it works
  "how.title": { es: "Cómo Funciona", en: "How It Works" },
  "how.subtitle": { es: "Intercambiar libros nunca fue tan fácil", en: "Swapping books has never been easier" },
  "how.step1.title": { es: "Comparte tus Libros", en: "List Your Books" },
  "how.step1.desc": {
    es: "Comparte en SwapyBook los libros que quieres intercambiar con su foto y descripción",
    en: "Share on SwapyBook the books you want to swap with photo and description",
  },
  "how.step2.title": { es: "Encuentra Lecturas", en: "Find Reads" },
  "how.step2.desc": {
    es: "Explora libros disponibles en tu ciudad y encuentra tu próxima lectura",
    en: "Browse available books in your city and find your next read",
  },
  "how.step3.title": { es: "¡Conecta e Intercambia!", en: "Connect & Swap!" },
  "how.step3.desc": {
    es: "Contacta al propietario, acuerden el encuentro e intercambien sus libros",
    en: "Contact the owner, arrange to meet, and swap your books",
  },
  "how.step4.title": { es: "Califica", en: "Rate" },
  "how.step4.desc": { es: "Cuéntanos cómo te pareció el intercambio", en: "Tell us how you found the exchange" },
  "how.cta": { es: "Crea Cuenta Gratis", en: "Create Free Account" },

  // Featured Books
  "featured.title": { es: "Libros Destacados", en: "Featured Books" },
  "featured.subtitle": {
    es: "Descubre los libros más populares disponibles para intercambio",
    en: "Discover the most popular books available for exchange",
  },
  "featured.viewAll": { es: "Ver Todos", en: "View All" },
  "featured.location": { es: "Ubicación", en: "Location" },
  "featured.condition": { es: "Estado", en: "Condition" },
  "featured.swap": { es: "Solicitar Intercambio", en: "Request Swap" },

  // Book conditions
  "condition.excellent": { es: "Excelente", en: "Excellent" },
  "condition.good": { es: "Bueno", en: "Good" },
  "condition.fair": { es: "Aceptable", en: "Fair" },

  // Community
  "community.title": { es: "Únete a Nuestra Comunidad", en: "Join Our Community" },
  "community.subtitle": {
    es: "Miles de lectores ya están intercambiando libros y creando conexiones",
    en: "Thousands of readers are already swapping books and making connections",
  },
  "community.testimonial1": {
    es: "SwapyBook me ha permitido descubrir autores increíbles sin gastar una fortuna. ¡La mejor comunidad de lectores!",
    en: "SwapyBook has allowed me to discover amazing authors without spending a fortune. The best reading community!",
  },
  "community.testimonial2": {
    es: "He conocido personas maravillosas que comparten mi pasión por la lectura. Cada intercambio es una nueva amistad.",
    en: "I've met wonderful people who share my passion for reading. Every swap is a new friendship.",
  },
  "community.testimonial3": {
    es: "Una forma sostenible y divertida de mantener mi biblioteca siempre renovada.",
    en: "A sustainable and fun way to keep my library always fresh.",
  },

  // CTA Section
  "cta.title": { es: "¿Listo para tu Próximo Intercambio?", en: "Ready for Your Next Swap?" },
  "cta.subtitle": {
    es: "Únete gratis y empieza a intercambiar libros hoy mismo",
    en: "Join for free and start swapping books today",
  },
  "cta.button": { es: "Crear Cuenta Gratis", en: "Create Free Account" },

  // Footer
  "footer.tagline": { es: "Conectando lectores, compartiendo historias", en: "Connecting readers, sharing stories" },
  "footer.explore": { es: "Explorar", en: "Explore" },
  "footer.about": { es: "Sobre Nosotros", en: "About Us" },
  "footer.help": { es: "Ayuda", en: "Help" },
  "footer.contact": { es: "Contacto", en: "Contact" },
  "footer.terms": { es: "Términos", en: "Terms" },
  "footer.privacy": { es: "Privacidad", en: "Privacy" },
  "footer.rights": { es: "Todos los derechos reservados", en: "All rights reserved" },

  // Auth
  "auth.loginTitle": { es: "Bienvenido de nuevo", en: "Welcome back" },
  "auth.loginSubtitle": {
    es: "Inicia sesión para tu Swapy de libros",
    en: "Sign in for your book Swapy",
  },
  "auth.signupTitle": { es: "Crear cuenta", en: "Create account" },
  "auth.signupSubtitle": {
    es: "Únete a la comunidad de intercambio de libros",
    en: "Join the book exchange community",
  },
  "auth.continueWithGoogle": { es: "Continuar con Google", en: "Continue with Google" },
  "auth.continueWithApple": { es: "Continuar con Apple", en: "Continue with Apple" },
  "auth.orContinueWith": { es: "o continúa con email", en: "or continue with email" },
  "auth.fullName": { es: "Nombre completo", en: "Full name" },
  "auth.email": { es: "Correo electrónico", en: "Email address" },
  "auth.password": { es: "Contraseña", en: "Password" },
  "auth.acceptTerms": { es: "Acepto los", en: "I accept the" },
  "auth.termsLink": { es: "Términos y Condiciones", en: "Terms and Conditions" },
  "auth.acceptPrivacy": { es: "Acepto la", en: "I accept the" },
  "auth.privacyLink": { es: "Política de Privacidad", en: "Privacy Policy" },
  "auth.loginButton": { es: "Iniciar sesión", en: "Sign in" },
  "auth.signupButton": { es: "Crear cuenta", en: "Create account" },
  "auth.noAccount": { es: "¿No tienes cuenta?", en: "Don't have an account?" },
  "auth.hasAccount": { es: "¿Ya tienes cuenta?", en: "Already have an account?" },
  "auth.signupLink": { es: "Regístrate", en: "Sign up" },
  "auth.loginLink": { es: "Inicia sesión", en: "Sign in" },
  "auth.error.title": { es: "Error", en: "Error" },
  "auth.error.invalidEmail": { es: "Email inválido", en: "Invalid email" },
  "auth.error.weakPassword": {
    es: "La contraseña debe tener al menos 8 caracteres",
    en: "Password must be at least 8 characters",
  },
  "auth.error.invalidName": {
    es: "El nombre debe tener al menos 2 caracteres",
    en: "Name must be at least 2 characters",
  },
  "auth.error.acceptTerms": {
    es: "Debes aceptar los términos y condiciones",
    en: "You must accept the terms and conditions",
  },
  "auth.error.acceptPrivacy": {
    es: "Debes aceptar la política de privacidad",
    en: "You must accept the privacy policy",
  },
  "auth.error.invalidCredentials": { es: "Email o contraseña incorrectos", en: "Invalid email or password" },
  "auth.error.alreadyRegistered": { es: "Este email ya está registrado", en: "This email is already registered" },
  "auth.success.loginTitle": { es: "¡Bienvenido!", en: "Welcome!" },
  "auth.success.loginDesc": { es: "Has iniciado sesión correctamente", en: "You have signed in successfully" },
  "auth.success.signupTitle": { es: "¡Cuenta creada!", en: "Account created!" },
  "auth.success.signupDesc": {
    es: "Tu cuenta ha sido creada. Revisa tu email para verificarla.",
    en: "Your account has been created. Check your email to verify it.",
  },
  "auth.logout": { es: "Cerrar sesión", en: "Log out" },

  // Settings
  "nav.settings": { es: "Configuración", en: "Settings" },
  "settings.title": { es: "Configuración", en: "Settings" },
  "settings.subtitle": { es: "Administra tu cuenta y preferencias", en: "Manage your account and preferences" },
  "settings.roleAdmin": { es: "Administrador", en: "Administrator" },
  "settings.roleUser": { es: "Usuario", en: "User" },
  "settings.userSettings": { es: "Configuración de usuario", en: "User Settings" },
  "settings.adminSettings": { es: "Administración", en: "Administration" },
  "settings.profile": { es: "Mi Perfil", en: "My Profile" },
  "settings.profileDesc": {
    es: "Edita tu información personal y foto de perfil",
    en: "Edit your personal information and profile picture",
  },
  "settings.notifications": { es: "Notificaciones", en: "Notifications" },
  "settings.notificationsDesc": {
    es: "Configura cómo y cuándo recibir alertas",
    en: "Configure how and when to receive alerts",
  },
  "settings.language": { es: "Idioma", en: "Language" },
  "settings.languageDesc": { es: "Selecciona el idioma de la aplicación", en: "Select the application language" },
  "settings.appearance": { es: "Apariencia", en: "Appearance" },
  "settings.appearanceDesc": { es: "Personaliza el tema y la visualización", en: "Customize the theme and display" },
  "settings.security": { es: "Seguridad", en: "Security" },
  "settings.securityDesc": {
    es: "Cambia tu contraseña y configura la seguridad",
    en: "Change your password and configure security",
  },
  "settings.adminUsers": { es: "Gestión de Usuarios", en: "User Management" },
  "settings.adminUsersDesc": { es: "Administra usuarios y sus roles", en: "Manage users and their roles" },
  "settings.adminLegal": { es: "Documentos Legales", en: "Legal Documents" },
  "settings.adminLegalDesc": {
    es: "Gestiona términos y políticas de privacidad",
    en: "Manage terms and privacy policies",
  },
  "settings.adminConfig": { es: "Configuración del Sistema", en: "System Configuration" },
  "settings.adminConfigDesc": { es: "Ajustes generales de la plataforma", en: "General platform settings" },

  // Profile
  "profile.title": { es: "Mi Perfil", en: "My Profile" },
  "profile.subtitle": { es: "Gestiona tu información personal", en: "Manage your personal information" },
  "profile.avatar": { es: "Foto de perfil", en: "Profile photo" },
  "profile.avatarDesc": {
    es: "Tu foto será visible para otros usuarios",
    en: "Your photo will be visible to other users",
  },
  "profile.avatarInfo": {
    es: "Haz clic en el icono de cámara para cambiar tu foto de perfil.",
    en: "Click the camera icon to change your profile photo.",
  },
  "profile.avatarComingSoon": {
    es: "La subida de avatar estará disponible próximamente",
    en: "Avatar upload will be available soon",
  },
  "profile.personalInfo": { es: "Información personal", en: "Personal information" },
  "profile.personalInfoDesc": { es: "Actualiza tu información de perfil", en: "Update your profile information" },
  "profile.email": { es: "Correo electrónico", en: "Email" },
  "profile.emailNote": { es: "El correo electrónico no se puede cambiar", en: "Email cannot be changed" },
  "profile.fullName": { es: "Nombre completo", en: "Full name" },
  "profile.fullNamePlaceholder": { es: "Ingresa tu nombre completo", en: "Enter your full name" },
  "profile.city": { es: "Ciudad", en: "City" },
  "profile.cityPlaceholder": { es: "Ingresa tu ciudad", en: "Enter your city" },
  "profile.cityDesc": {
    es: "Tu ciudad ayuda a encontrar intercambios cercanos",
    en: "Your city helps find nearby exchanges",
  },
  "profile.bio": { es: "Biografía", en: "Bio" },
  "profile.bioPlaceholder": {
    es: "Cuéntanos sobre ti y tus gustos literarios...",
    en: "Tell us about yourself and your literary tastes...",
  },
  "profile.characters": { es: "caracteres", en: "characters" },
  "profile.preferredLanguage": { es: "Idioma preferido", en: "Preferred language" },
  "profile.selectLanguage": { es: "Selecciona un idioma", en: "Select a language" },
  "profile.languageDesc": {
    es: "El idioma en que se mostrará la aplicación",
    en: "The language the app will be displayed in",
  },
  "profile.cancel": { es: "Cancelar", en: "Cancel" },
  "profile.save": { es: "Guardar cambios", en: "Save changes" },
  "profile.error.loadTitle": { es: "Error al cargar", en: "Error loading" },
  "profile.error.loadDesc": { es: "No se pudo cargar tu perfil", en: "Could not load your profile" },
  "profile.error.saveTitle": { es: "Error al guardar", en: "Error saving" },
  "profile.error.saveDesc": { es: "No se pudieron guardar los cambios", en: "Could not save changes" },
  "profile.success.title": { es: "¡Perfil actualizado!", en: "Profile updated!" },
  "profile.success.desc": { es: "Tus cambios han sido guardados", en: "Your changes have been saved" },
  "profile.photoUpdated": { es: "Foto actualizada", en: "Photo updated" },
  "profile.photoError": { es: "Error al subir foto", en: "Error uploading photo" },
  "profile.uploadPhoto": { es: "Subir foto", en: "Upload photo" },
  "profile.useCamera": { es: "Usar cámara", en: "Use camera" },
  "profile.useLink": { es: "Usar enlace", en: "Use link" },

  // CTA Section
  "cta.badge": { es: "Crea tu cuenta 100% Gratis", en: "Create your 100% Free account" },
  "cta.noMonthlyFees": { es: "Sin cargos mensuales", en: "No monthly fees" },
  "cta.setupIn2Min": { es: "Configuración en 2 minutos", en: "Setup in 2 minutes" },
  "cta.cancelAnytime": { es: "Cancela cuando quieras", en: "Cancel anytime" },

  // Notifications
  "notifications.title": { es: "Notificaciones", en: "Notifications" },
  "notifications.unread": { es: "sin leer", en: "unread" },
  "notifications.noNotifications": { es: "Sin notificaciones", en: "No notifications" },
  "notifications.noNotificationsDesc": { es: "Recibirás notificaciones cuando alguien responda a tus intercambios.", en: "You will receive notifications when someone responds to your exchanges." },
  "notifications.unreadSection": { es: "Sin leer", en: "Unread" },
  "notifications.readSection": { es: "Leídas", en: "Read" },
  "notifications.markAsRead": { es: "Marcar como leído", en: "Mark as read" },
  "notifications.markAllRead": { es: "Marcar todo leído", en: "Mark all read" },
  "notifications.delete": { es: "Eliminar", en: "Delete" },
  "notifications.deleteRead": { es: "Eliminar leídas", en: "Delete read" },
  "notifications.viewExchange": { es: "Ver intercambio →", en: "View exchange →" },

  // Auth - Reset Password
  "auth.forgotPassword": { es: "¿Olvidaste tu contraseña?", en: "Forgot your password?" },
  "auth.resetPassword": { es: "Recuperar contraseña", en: "Reset password" },
  "auth.resetPasswordDesc": { es: "Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.", en: "Enter your email and we will send you a link to reset your password." },
  "auth.sendLink": { es: "Enviar enlace", en: "Send link" },
  "auth.emailSent": { es: "Correo enviado", en: "Email sent" },
  "auth.emailSentDesc": { es: "Revisa tu correo para restablecer tu contraseña", en: "Check your email to reset your password" },

  // Common
  "common.noName": { es: "Sin nombre", en: "No name" },
  "common.error": { es: "Error", en: "Error" },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("swapybook-language");
    return (saved as Language) || "es";
  });

  useEffect(() => {
    localStorage.setItem("swapybook-language", language);
  }, [language]);

  const t = (key: string): string => {
    const translation = translations[key];
    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    return translation[language];
  };

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
